import { notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { ContentHeadline } from "~/components/content/content-headline";
import type { Props as EntityLinkProps } from "~/components/entity-link";
import { useImageUtils } from "~/lib/hooks";
import { formatDistanceToNow, toISOString } from "~/lib/utils";
import type { genericArticleFooterItemArticle$key } from "~/queries/__generated__/genericArticleFooterItemArticle.graphql";

import type { ContainerProps } from "./styles";
import {
  Container,
  DateContainer,
  Headline,
  ImageContainer,
  StyledBaseImage,
  StyledEntityLink,
} from "./styles";

export type Props = {
  onClick: () => void;
  reference: genericArticleFooterItemArticle$key;
  size?: ContainerProps["$size"];
} & Pick<EntityLinkProps, "query">;

export const GenericArticleFooterItem: FunctionComponent<Props> = ({
  onClick,
  query,
  reference: reference_,
  size = "normal",
}) => {
  const { getResizedImageUrl } = useImageUtils();

  const content = useFragment(
    graphql`
      fragment genericArticleFooterItemArticle on Article {
        ...entityLink
        ...contentHeadlineContent
        updatedDate
        images {
          isSlideshow
          title
          url
        }
      }
    `,
    reference_,
  );

  const renderMainImage = () => {
    const images = content.images?.filter(img => notEmpty(img)) ?? [];
    const mainImage = images.find(img => !!img?.isSlideshow) ?? first(images);
    const source = mainImage?.url ? getResizedImageUrl(mainImage?.url, 800) : mainImage?.url;
    const alt = mainImage?.title ?? "";

    return (
      <StyledBaseImage
        $aspectRatio="4 / 3"
        $height="auto"
        $objectFit="cover"
        $width="100%"
        alt={alt}
        src={source}
        disableLinkingData
      />
    );
  };

  return (
    <StyledEntityLink query={query} reference={content} onClick={onClick}>
      <Container $size={size}>
        <ImageContainer>{renderMainImage()}</ImageContainer>
        <Headline>
          <ContentHeadline reference={content} />
        </Headline>
        <DateContainer>
          <time dateTime={toISOString(content.updatedDate as number)}>
            {formatDistanceToNow(content.updatedDate as number)}
          </time>
        </DateContainer>
      </Container>
    </StyledEntityLink>
  );
};

GenericArticleFooterItem.displayName = "GenericArticleFooterItem";
